import { useEffect } from "react";
import { Link } from "react-router-dom";
import { FiFile, FiUpload, FiClock, FiCheckCircle, FiClipboard, FiArrowRight } from "react-icons/fi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useUser } from "@/features/auth";
import { useDocuments } from "./_hooks/useDocuments";
import { documentCategoryLabels, documentStatusConfig } from "./_types/portal.types";
import { CardSkeleton } from "@/components/common";

export function DashboardPage() {
  const user = useUser();
  const { documents, isLoading, fetchDocuments } = useDocuments();

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Calculate stats
  const totalDocuments = documents.length;
  const pendingDocuments = documents.filter((d) => d.status === "pending").length;
  const approvedDocuments = documents.filter((d) => d.status === "approved").length;
  const recentDocuments = documents.slice(0, 5);

  return (
    <div className="space-y-8">
      {/* Welcome section */}
      <div>
        <h1 className="font-serif text-3xl font-medium text-primary">
          Welcome back, {user?.name?.split(" ")[0] || "there"}
        </h1>
        <p className="text-muted-foreground mt-1">
          Here's an overview of your document portal
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <FiFile className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-semibold">
                  {isLoading ? "-" : totalDocuments}
                </p>
                <p className="text-sm text-muted-foreground">Total Documents</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-full">
                <FiClock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold">
                  {isLoading ? "-" : pendingDocuments}
                </p>
                <p className="text-sm text-muted-foreground">Pending Review</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-full">
                <FiCheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold">
                  {isLoading ? "-" : approvedDocuments}
                </p>
                <p className="text-sm text-muted-foreground">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Fact Finder CTA */}
      <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <FiClipboard className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Complete Your Fact Finder</h3>
                <p className="text-muted-foreground text-sm mt-1">
                  Help us find the best loan options by providing your financial information securely.
                </p>
              </div>
            </div>
            <Button asChild className="btn-pill whitespace-nowrap">
              <Link to="/portal/fact-finder">
                Start Form
                <FiArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Quick actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Button asChild className="btn-pill">
              <Link to="/portal/documents">
                <FiUpload className="mr-2 h-4 w-4" />
                Upload Document
              </Link>
            </Button>
            <Button asChild variant="outline" className="btn-pill">
              <Link to="/portal/profile">
                Update Profile
              </Link>
            </Button>
            <Button asChild variant="outline" className="btn-pill">
              <Link to="/contact">
                Contact Support
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent documents */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Recent Documents</CardTitle>
          <Button asChild variant="ghost" size="sm">
            <Link to="/portal/documents">View all</Link>
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </div>
          ) : recentDocuments.length === 0 ? (
            <div className="text-center py-8">
              <FiFile className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No documents yet</p>
              <Button asChild className="mt-4 btn-pill">
                <Link to="/portal/documents">
                  <FiUpload className="mr-2 h-4 w-4" />
                  Upload your first document
                </Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {recentDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-muted/50"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 bg-background rounded">
                      <FiFile className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{doc.originalFilename}</p>
                      <p className="text-xs text-muted-foreground">
                        {documentCategoryLabels[doc.category]} &bull;{" "}
                        {new Date(doc.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant="secondary"
                    className={documentStatusConfig[doc.status].color}
                  >
                    {documentStatusConfig[doc.status].label}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
